/**
 * @coeldery/page-flip · **書頁紙疊**（`index.tsx` 分出；硬規則：模組逐檔 ≤250 行）
 *
 * ⭐ 1.3.0（產品負責人 2026-09-24：「the page flip still not good enough, change until i ok」）：
 *   · 底層＝**鄰頁**（跟手時透出 —— 似真書掀開一頁，下面嗰頁露出嚟）
 *   · **紙纖維質感**（`.pf-paper`：兩層極淡斜線，純 CSS，零圖像）
 *   · **掀邊陰影**（跟手加深 → 似紙彎曲）
 *   · 面層頁：跟手 `translateX` ＋ `rotateY`（±16°）＋ 換頁入場動畫（`pf-turn-in`）
 *   ⚠️ 動畫全部由本檔注入（父層唔使管 CSS；class 名前綴 `pf-` 唔會撞）
 *   ⚠️ `reduce`（`prefers-reduced-motion: reduce`）→ **完全唔動畫**，但仍然可以掀
 *   ❗ 模組**唔識**產品內容：三層（面頁／鄰頁）嘅 `node` 一律由 `index.tsx` 傳入
 */
import type { ReactNode } from 'react';

const KEYFRAMES = `
@keyframes pf-turn-in { from { opacity: .35; transform: translateX(var(--pf-from, 0)) rotateY(var(--pf-rot, 0deg)); } to { opacity: 1; transform: translateX(0) rotateY(0deg); } }
.pf-paper { background-image: repeating-linear-gradient(90deg, rgba(0,0,0,.020) 0 1px, transparent 1px 7px), repeating-linear-gradient(0deg, rgba(0,0,0,.014) 0 1px, transparent 1px 9px); }
`;

export interface FlipSheetProps {
  /** 跟手位移（px；>0 ＝ 掀返之前） */
  drag: number;
  /** `prefers-reduced-motion: reduce` → 唔動畫 */
  reduce: boolean;
  /** 最近掀嘅方向（>0 ＝ 掀去之後） */
  turn: number;
  /** 面層頁（`page.key`；換 key ＝ 重播入場動畫） */
  pageKey: string;
  page: ReactNode;
  /** 底層鄰頁 */
  peekNode: ReactNode;
  peekOn: boolean;
}

export default function FlipSheet({ drag, reduce, turn, pageKey, page, peekNode, peekOn }: FlipSheetProps) {
  return (
    <div
      style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', background: 'var(--color-card, transparent)' }}
    >
      <style>{KEYFRAMES}</style>
      {/* 底層：**鄰頁**（跟手時透出） */}
      <div
        aria-hidden
        data-testid="flip-peek"
        style={{
          position: 'absolute',
          inset: 0,
          opacity: peekOn ? 1 : 0,
          transition: 'opacity 140ms linear',
          pointerEvents: 'none',
          filter: 'brightness(.93)',
        }}
      >
        {peekNode}
      </div>
      {/* 紙纖維質感（極淡） */}
      <div aria-hidden className="pf-paper" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.75 }} />
      {/* 掀邊陰影（掀邊嗰邊加深 → 似紙彎曲；放手淡走） */}
      <div
        aria-hidden
        data-testid="flip-shade"
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          opacity: Math.min(0.9, Math.abs(drag) / 140),
          transition: drag === 0 ? 'opacity 260ms ease-out' : 'none',
          background:
            drag > 0
              ? 'linear-gradient(to right, rgba(0,0,0,.26), rgba(0,0,0,.06) 18%, transparent 42%)'
              : 'linear-gradient(to left, rgba(0,0,0,.26), rgba(0,0,0,.06) 18%, transparent 42%)',
        }}
      />
      {/* 面層：跟手頁（＋ 換頁入場動畫） */}
      <div
        key={pageKey}
        data-testid="flip-sheet"
        style={{
          position: 'relative',
          transform: `translateX(${drag}px) rotateY(${Math.max(-16, Math.min(16, drag / 10))}deg)`,
          transformOrigin: drag < 0 ? 'left center' : 'right center',
          transition: drag === 0 ? 'transform 320ms cubic-bezier(0.22, 0.61, 0.36, 1)' : 'none',
          willChange: 'transform',
          boxShadow: drag === 0 ? '0 1px 2px rgba(0,0,0,.06)' : '0 18px 34px rgba(0,0,0,.24)',
          borderRadius: '10px',
          background: 'var(--color-card, transparent)',
          animation: reduce ? undefined : 'pf-turn-in 320ms cubic-bezier(0.22, 0.61, 0.36, 1)',
          ...({
            '--pf-from': turn > 0 ? '34px' : '-34px',
            '--pf-rot': turn > 0 ? '-4deg' : '4deg',
          } as React.CSSProperties),
        }}
      >
        {page}
      </div>
    </div>
  );
}
